/** @jsx jsx */
import React, { use } from 'react';
import { Themed } from '@theme-ui/mdx';
import { Box, jsx } from 'theme-ui';
import Button from './Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../utils/api';
import { useDispatch } from 'react-redux';
import { buyParcel, buySeriesShipment } from '../state/parcelFlow';
import { Flex } from 'theme-ui';
import { imagePathForProduct, multiParcelProduct } from '../state/catalog';
import { formatPrice } from '../utils/formatters';
import moment from 'moment-timezone';
import Spinner from './Spinner';

const mapCountryCodes = (products) => {
  const codes = products
    .map((product) => product?.countryCode)
    .filter((code, index, self) => code && self.indexOf(code) === index);

  const hasMissingCountryCode = products.some((product) => !product?.countryCode);
  if (hasMissingCountryCode && !codes.includes('FI')) {
    codes.push('FI');
  }

  return codes;
};

export default ({ products = [] }) => {
  const { i18n } = useTranslation();
  const locale = useMemo(() => i18n.language, [i18n]);

  const [fetchedProducts, setFetchedProducts] = useState({});

  useEffect(() => {
    (async () => {
      const resultByCountry = {};
      const countryCodes = mapCountryCodes(products);
      await Promise.all(
        countryCodes.map(async (code) => {
          try {
            let result;
            if (code === 'FI') {
              result = await api.getProducts();
              resultByCountry[code] = result || [];
            } else {
              result = await api.getProductsForCountry(code, locale);
              resultByCountry[code] = result?.products || [];
            }
          } catch (error) {
            console.error(`Failed to fetch products for ${code}:`, error);
            resultByCountry[code] = [];
          }
        })
      );

      setFetchedProducts(resultByCountry);
    })();
  }, []);

  const productList = products.map((product) => {
    const countryCode = product?.countryCode || 'FI';
    const fetchedProduct = fetchedProducts?.[countryCode] || [];
    const productData = fetchedProduct.find((p) => p.id === product.productId);

    return {
      ...product,
      ...productData,
    };
  });

  if (Object.values(fetchedProducts).length === 0) {
    return (
      <div sx={{ position: 'relative', height: '100px', maxWidth: 640 }}>
        <Spinner />
      </div>
    );
  }
  return (
    <Flex sx={{ flexDirection: 'column', width: '100%', gap: 3, mb: 4 }}>
      {productList.map((product, i) => (
        <Product {...(product?.productType === 'Sarjapaketti' ? multiParcelProduct(product) : product)} key={i} />
      ))}
    </Flex>
  );
};

const Product = ({
  productType,
  productId,
  countryCode = 'FI',
  specialOffer,
  displayName,
  maxSize,
  price,
  mailboxDelivery,
}) => {
  const isSeriesProduct = productType === 'Sarjapaketti';
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onClick = useCallback(() => {
    !isSeriesProduct
      ? dispatch(buyParcel(productId, countryCode || 'FI'))
      : dispatch(buySeriesShipment(true, productId, countryCode || 'FI'));
  }, [dispatch, productId, countryCode, isSeriesProduct]);

  const size = useMemo(() => {
    if (!maxSize) return null;
    return `${maxSize.height} x ${maxSize.width} x ${maxSize.depth}`;
  }, [maxSize]);

  const productPrice = useMemo(() => {
    if (!price) return null;
    return formatPrice(price);
  }, [price]);

  if (!displayName || !productPrice) {
    return null;
  }

  return (
    <Flex
      sx={{
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'border',
        flexDirection: 'column',
        width: '100%',
        maxWidth: [null, null, null, 750],
        position: 'relative',
      }}
    >
      <Flex
        sx={{
          position: 'absolute',
          top: 0,
          left: ['auto', null, 0],
          right: [0, null, 'auto'],
        }}
      >
        {specialOffer && <Offer {...specialOffer} isSeriesProduct={isSeriesProduct} />}
        {mailboxDelivery && <Label color="primary" text={t('buyParcel.services.JPL.title')} />}
      </Flex>
      <Flex sx={{ pl: [2, null, 5], py: [4, null, '23px'], px: 3 }}>
        <ProductInfo
          id={productId}
          displayName={isSeriesProduct ? `10 x ${displayName}` : displayName}
          maxSize={size}
          onClick={onClick}
          price={productPrice}
          isSeriesProduct={isSeriesProduct}
        />
      </Flex>
    </Flex>
  );
};

const ProductInfo = ({ id, displayName, maxSize, price, onClick }) => {
  const { t } = useTranslation();
  return (
    <Flex
      sx={{
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Flex sx={{ flex: 1, display: ['none', null, 'flex'] }}>
        <span
          sx={{
            fontWeight: 'bold',
            hyphens: 'auto',
          }}
        >
          {displayName}
        </span>
      </Flex>

      <Flex
        sx={{
          flex: 1,
          flexDirection: ['column', null, 'row'],
          alignItems: 'center',
          gap: 2,
        }}
      >
        <img src={imagePathForProduct(id)} alt={displayName} height="60" width="60" />
      </Flex>

      <Flex sx={{ flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span
          sx={{
            textAlign: 'center',
            fontSize: 3,
            fontWeight: 'bold',
            hyphens: 'auto',
            display: ['flex', null, 'none'],
          }}
        >
          {displayName}
        </span>
        <span sx={{ textAlign: 'center', fontWeight: 'semibold', fontSize: 1, mt: [12, null, 0] }}>
          {t('buyParcel.product.maxSize')}
        </span>
        <span sx={{ textAlign: 'center', fontSize: [1, null, null, 2] }}>{maxSize} cm</span>
      </Flex>

      <Flex sx={{ flex: 1, justifyContent: 'flex-end' }}>
        <Button variant="small" onClick={onClick}>
          {price}
        </Button>
      </Flex>
    </Flex>
  );
};

const Offer = (specialOffer) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const offer = useMemo(() => {
    if (!specialOffer) return null;

    const now = moment();

    const isValidParcelType = specialOffer.isSeriesProduct === (specialOffer.parcelType === 'multiParcel');
    const isActive = now.isBetween(specialOffer.startTime, specialOffer.endTime);

    return isValidParcelType && isActive ? specialOffer : null;
  }, [specialOffer]);

  if (!offer) return null;

  return <Label color={offer?.color} text={offer?.text?.[locale || 'fi']} />;
};

const Label = ({ color, text }) => {
  return (
    <Flex
      sx={{
        bg: color || 'primary',
        color: 'white',
        borderRadius: 1,
        fontSize: '11px',
        mx: 1,
        mt: 1,
        py: 1,
        px: 2,
      }}
    >
      <span>{text}</span>
    </Flex>
  );
};
